import type { ListJobTasksResponse } from '@vy/api-definition';
import { raiseUnreachable } from '@vy/err';
import { JOB_TASK_STATUSES, JOB_TASK_TYPES } from '@vy/jobs-types';
import { JobTaskStatusUnreachableError } from '../feedback-errors/job-task-status-unreachable-error';

type JobTask = ListJobTasksResponse[number];

type ModifiedJobTask = Omit<JobTask, 'type'> & {
  details: string;
  type: string;
};

export function mapJobTaskToNotification(jobTask: JobTask): ModifiedJobTask {
  return {
    ...jobTask,
    details: getTaskDetails(jobTask),
  };
}

export function getTaskNavigationUrl(task: JobTask): string | null {
  const metadata = task.metadata as Record<string, unknown>;

  if (
    task.type === JOB_TASK_TYPES.StripeExportCustomer ||
    task.type === JOB_TASK_TYPES.NetSuiteExportCustomer ||
    task.type === JOB_TASK_TYPES.LinkMorningCustomer ||
    task.type === JOB_TASK_TYPES.LinkNetSuiteCustomer
  ) {
    const customerId = metadata.customerId as string | undefined;

    if (customerId) {
      return `/customers/${customerId}/overview`;
    }
  }

  if (
    task.type === JOB_TASK_TYPES.NetSuiteExportInvoice ||
    task.type === JOB_TASK_TYPES.NetSuiteExportInvoiceSalesOrder ||
    task.type === JOB_TASK_TYPES.StripeExportInvoice ||
    task.type === JOB_TASK_TYPES.QuickBooksExportInvoice ||
    task.type === JOB_TASK_TYPES.XeroExportInvoice ||
    task.type === JOB_TASK_TYPES.SendInvoiceEmail
  ) {
    const invoiceId = metadata.invoiceId as string | undefined;
    const customerId = metadata.customerId as string | undefined;

    if (invoiceId && customerId) {
      return `/invoices/${invoiceId}/customer/${customerId}/dynamic`;
    }
  }

  if (task.type === JOB_TASK_TYPES.NetsuiteExportSalesOrder) {
    const contractId = metadata.contractId as string | undefined;

    if (contractId) {
      return `/contracts/${contractId}`;
    }
  }

  return null;
}

function getTaskDetails(task: JobTask): string {
  switch (task.status) {
    case JOB_TASK_STATUSES.Failed:
      return buildFailedTaskDetails(task);
    case JOB_TASK_STATUSES.Completed:
      return buildCompletedTaskDetails(task);
    case JOB_TASK_STATUSES.Pending:
    case JOB_TASK_STATUSES.InProgress:
      return 'Task is in progress';
    case JOB_TASK_STATUSES.Skipped:
      return 'Task was skipped';
    case JOB_TASK_STATUSES.Blocked:
      return 'Task is blocked';
    default:
      return raiseUnreachable(task.status, new JobTaskStatusUnreachableError(task.status));
  }
}

function buildFailedTaskDetails(task: JobTask): string {
  const baseMessage = task.errorMessage ?? 'Unknown error';
  const contextSuffix = buildContextSuffix(task);

  if (!contextSuffix) {
    return baseMessage;
  }

  return `${baseMessage} — ${contextSuffix}`;
}

function buildCompletedTaskDetails(task: JobTask): string {
  const baseMessage = 'Task completed successfully';
  const contextSuffix = buildContextSuffix(task);

  if (!contextSuffix) {
    return baseMessage;
  }

  return `${baseMessage} — ${contextSuffix}`;
}

function buildContextSuffix(task: JobTask): string | null {
  const metadata = task.metadata as Record<string, unknown>;
  const customerName = metadata.customerName as string | undefined;
  const invoiceNumber = metadata.invoiceNumber as string | undefined;
  const contractName = metadata.contractName as string | undefined;

  const parts: string[] = [];

  if (customerName) {
    parts.push(`customer: ${customerName}`);
  }

  if (invoiceNumber) {
    parts.push(`invoice #${invoiceNumber}`);
  } else if (contractName) {
    parts.push(`contract: ${contractName}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join(', ');
}
